<script setup lang="ts">
import { ArrowLeft, ArrowUpRight, RefreshCw, RotateCcw, Workflow } from '@lucide/vue'
import {
    Button,
    Separator,
    ZhJson,
    ZhKeyValue,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusDot,
} from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { api } from '@/api'
import CopyButton from '@/components/CopyButton.vue'
import WebhookStatus from '@/components/WebhookStatus.vue'
import { useLiveResource } from '@/composables/useLiveResource'
import { requireAiAccount } from '@/lib/aiAccountGuard'

const route = useRoute()
const router = useRouter()
const id = computed(() => String(route.params.id))
const {
    data: webhook,
    error,
    loading,
    refreshing,
    now,
    visible,
    refresh,
} = useLiveResource(
    () => id.value,
    signal => api.webhook(id.value, signal),
)
const replaying = ref(false)
const replayError = ref<string | null>(null)
// Keep the same key after a lost response: retrying must recover that replay, not create another.
const replayKey = ref<string | null>(null)
watch(id, () => {
    replaying.value = false
    replayError.value = null
    replayKey.value = null
})
async function replay() {
    if (replaying.value || !webhook.value?.replayable) return
    const sourceId = id.value
    replaying.value = true
    replayError.value = null
    // #1796: a replay starts the run (or decides the gate) the request names, which is model work, so it asks for the
    // AI account first. The button is already disabled while it asks, so a second click cannot send a second replay.
    if (!(await requireAiAccount())) {
        if (sourceId === id.value) replaying.value = false
        return
    }
    replayKey.value ??= crypto.randomUUID()
    try {
        const result = await api.replayWebhook(sourceId, replayKey.value)
        if (sourceId === id.value) await router.push(`/webhook/${encodeURIComponent(result.id)}`)
    } catch (caught) {
        if (sourceId === id.value) replayError.value = replayFailureMessage(caught)
    } finally {
        if (sourceId === id.value) replaying.value = false
    }
}
function replayFailureMessage(caught: unknown): string {
    const code = caught instanceof Error ? caught.message : ''
    switch (code) {
        case 'webhook_payload_unavailable':
            return 'The stored request payload can no longer be decrypted. Send a new webhook request to replay this work.'
        case 'webhook_not_found':
            return 'This webhook is no longer available. Return to webhook history to choose another request.'
        case 'Unauthorized':
        case '401':
            return 'Your session has expired. Sign in again before replaying this webhook.'
        case 'Forbidden':
        case '403':
            return 'You do not have permission to replay this webhook.'
        case 'webhook_not_replayable':
            return 'This webhook cannot be replayed. Refresh its details to see why.'
        case 'replay_idempotency_key_reused':
        case 'replay_idempotency_key_required':
            return 'The replay request could not be created. Reopen this webhook before trying again.'
        default:
            return 'Could not confirm the replay. Try again to retrieve the same attempt without sending it twice.'
    }
}
const duration = computed(() => {
    if (!webhook.value) return '—'
    const ms = Math.max(0, webhook.value.durationMs ?? now.value - new Date(webhook.value.receivedAt).getTime())
    return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`
})
const payloads = computed(() =>
    webhook.value
        ? [
              { key: 'request', title: 'Request body', value: webhook.value.request.body },
              { key: 'response', title: 'Response body', value: webhook.value.response.body },
          ]
        : [],
)
function json(value: unknown) {
    return JSON.stringify(value ?? null, null, 2)
}
function date(at: string) {
    return new Date(at).toLocaleString()
}
</script>

<template>
    <section class="w-full min-w-0 space-y-5 p-4 md:p-8">
        <RouterLink to="/webhooks" class="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"><ArrowLeft :size="15" /> Webhooks</RouterLink>
        <p v-if="loading && !webhook" role="status" class="text-muted-foreground py-16 text-center text-sm">Loading webhook…</p>
        <ZhPanel v-if="error" as="div" tone="failed" role="alert" class="flex flex-wrap items-center justify-between gap-3 text-sm">
            <span>{{ webhook ? 'Live updates interrupted. The last received state is shown below.' : 'Could not load this webhook. It may no longer be available.' }}</span>
            <Button variant="outline" size="sm" :disabled="refreshing" @click="refresh">Try again</Button>
        </ZhPanel>
        <template v-if="webhook">
            <header class="flex flex-wrap items-start justify-between gap-4">
                <div class="min-w-0 space-y-2">
                    <div class="flex flex-wrap items-center gap-3"><ZhPageTitle kicker="Webhook">{{ webhook.target.name }}</ZhPageTitle><WebhookStatus :status="webhook.status" /></div>
                    <p class="text-muted-foreground text-xs">{{ webhook.target.kind === 'task' ? 'Task trigger' : 'Gate decision' }} · Received {{ date(webhook.receivedAt) }}</p>
                    <p class="text-muted-foreground break-all font-mono text-xs">{{ webhook.id }}</p>
                </div>
                <div class="flex items-center gap-3">
                    <span class="text-muted-foreground flex items-center gap-1.5 text-xs"><ZhStatusDot :state="error ? 'active' : visible ? 'complete' : 'inactive'" :pulse="visible" />{{ error ? 'Reconnecting…' : visible ? 'Live · every second' : 'Updates paused' }}</span>
                    <Button variant="outline" size="icon-sm" aria-label="Refresh webhook" :disabled="refreshing" @click="refresh"><RefreshCw :size="15" /></Button>
                </div>
            </header>

            <ZhPanel v-if="webhook.replayOfId" as="div" class="flex flex-wrap items-center gap-2 text-sm"><RotateCcw :size="15" class="text-muted-foreground" /><span>This is a replay of <RouterLink :to="`/webhook/${webhook.replayOfId}`" class="text-primary underline underline-offset-2">webhook {{ webhook.replayOfId.slice(0, 8) }}</RouterLink>.</span></ZhPanel>

            <!-- Delivery and replay sit side by side from `lg`, the replay panel at a fixed width, and stack below it. -->
            <div class="flex flex-col gap-4 lg:flex-row lg:items-start">
                <ZhPanel as="article" class="min-w-0 space-y-4 lg:flex-1">
                    <div class="flex flex-wrap items-center justify-between gap-3"><ZhSectionHeading>Delivery</ZhSectionHeading><RouterLink v-if="webhook.target.id && webhook.target.kind === 'task'" :to="`/tasks/${webhook.target.id}`" class="text-primary inline-flex items-center gap-1 text-xs">Open task <ArrowUpRight :size="13" /></RouterLink></div>
                    <!-- The method keeps its green on purpose: `success-green` is the shared BG §18.4 palette token the kit serves, not a screen-local value (#1253, CTO decision 1). Only the line's own tint and radius were screen-local, and those are gone. -->
                    <p class="flex items-start gap-3 text-xs"><span class="shrink-0 font-semibold text-success-green">{{ webhook.request.method }}</span><code class="min-w-0 break-all">{{ webhook.request.path }}</code></p>
                    <dl>
                        <ZhKeyValue label="HTTP response">{{ webhook.response.status ?? 'Pending' }}</ZhKeyValue>
                        <ZhKeyValue label="Duration">{{ duration }}</ZhKeyValue>
                        <ZhKeyValue label="Completed">{{ webhook.completedAt ? date(webhook.completedAt) : 'In progress' }}</ZhKeyValue>
                    </dl>
                    <Separator />
                    <div v-if="webhook.executionId">
                        <!-- A link, drawn as the kit Button: the RouterLink is the element, the Button its look. -->
                        <Button as-child variant="outline" class="w-full justify-between"><RouterLink :to="`/executions/${webhook.executionId}`"><span class="flex items-center gap-2"><Workflow :size="18" /> View execution</span><ArrowUpRight :size="16" /></RouterLink></Button>
                        <p class="text-muted-foreground mt-2 text-xs">Follow the task runs and handoffs linked to this webhook.<RouterLink v-if="webhook.runId" :to="`/runs/${webhook.runId}`" class="hover:text-foreground ml-1 underline underline-offset-2">Open linked run</RouterLink></p>
                    </div>
                    <p v-else class="text-muted-foreground text-xs">No execution is linked to this request.</p>
                </ZhPanel>
                <ZhPanel as="aside" class="space-y-3 lg:w-80 lg:shrink-0" aria-label="Replay webhook">
                    <ZhSectionHeading class="items-center"><RotateCcw :size="15" /> Replay webhook</ZhSectionHeading>
                    <p class="text-muted-foreground text-sm">Send the original request again. A separate receipt records the response and links back to this one.</p>
                    <p v-if="webhook.replayable" class="text-muted-foreground text-xs">{{ webhook.target.kind === 'task' ? 'This can start a new task run. Normal overlap and execution checks still apply.' : 'The original gate decision will be submitted again. A gate already decided will not be decided twice.' }}</p>
                    <Button class="w-full" :disabled="replaying || !webhook.replayable" data-testid="replay-webhook" @click="replay"><RotateCcw :size="14" :class="replaying ? 'motion-safe:animate-spin' : ''" />{{ replaying ? 'Replaying…' : replayError ? 'Retry replay' : 'Replay webhook' }}</Button>
                    <p v-if="!webhook.replayable" class="text-muted-foreground text-xs" data-testid="replay-disabled-reason">{{ webhook.replayDisabledReason || 'This request cannot be replayed.' }}</p>
                    <p v-if="replayError" role="alert" class="text-destructive text-xs">{{ replayError }}</p>
                </ZhPanel>
            </div>

            <div class="grid min-w-0 gap-4 xl:grid-cols-2">
                <ZhPanel v-for="payload in payloads" :key="payload.key" as="article" flush class="min-w-0 overflow-hidden" :data-testid="`${payload.key}-payload`">
                    <header class="flex items-center justify-between gap-3 px-4 py-3"><ZhSectionHeading>{{ payload.title }}</ZhSectionHeading><CopyButton :value="json(payload.value)" :label="payload.title.toLowerCase()" /></header>
                    <Separator />
                    <ZhJson :value="payload.value" :label="payload.title" />
                </ZhPanel>
            </div>
            <p class="text-muted-foreground text-xs">Credential fields are redacted in the displayed request and response. Replay uses the original stored payload.</p>

            <ZhPanel as="details" flush class="overflow-hidden">
                <summary class="cursor-pointer px-4 py-3 text-sm font-medium">Request headers</summary>
                <Separator />
                <div class="flex justify-end px-4 pt-3"><CopyButton :value="json(webhook.request.headers)" label="request headers" /></div>
                <ZhJson :value="webhook.request.headers" label="Request headers" />
            </ZhPanel>

            <ZhPanel as="article" flush class="overflow-hidden">
                <header class="flex items-center justify-between gap-3 px-4 py-3"><ZhSectionHeading>Delivery log</ZhSectionHeading><CopyButton :value="json(webhook.events)" label="delivery log" /></header>
                <Separator />
                <p v-if="!webhook.events.length" class="text-muted-foreground p-4 text-sm">Waiting for processing events…</p>
                <ol v-else class="divide-y" aria-label="Delivery log">
                    <li v-for="(event, index) in webhook.events" :key="`${index}:${event.at}`" class="flex flex-col gap-2 px-4 py-3 text-xs sm:flex-row" :data-log-level="event.level">
                        <time :datetime="event.at" class="text-muted-foreground tabular-nums sm:w-45 sm:shrink-0">{{ date(event.at) }}</time>
                        <span class="font-mono uppercase sm:w-12 sm:shrink-0" :class="event.level === 'error' ? 'text-destructive' : 'text-muted-foreground'">{{ event.level }}</span>
                        <span class="min-w-0 flex-1 whitespace-pre-wrap break-words">{{ event.message }}</span>
                    </li>
                </ol>
            </ZhPanel>

            <ZhPanel v-if="webhook.replays.length" as="article" flush class="overflow-hidden">
                <ZhSectionHeading class="px-4 py-3">Replays<template #count>{{ webhook.replays.length }}</template></ZhSectionHeading>
                <Separator />
                <ul class="divide-y" aria-label="Replay history"><li v-for="attempt in webhook.replays" :key="attempt.id" class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"><RouterLink :to="`/webhook/${attempt.id}`" class="hover:text-primary inline-flex items-center gap-2"><RotateCcw :size="13" /><span class="font-mono text-xs">{{ attempt.id.slice(0, 8) }}</span><span class="text-muted-foreground text-xs">{{ date(attempt.receivedAt) }}</span></RouterLink><div class="flex items-center gap-3"><WebhookStatus :status="attempt.status" /><RouterLink v-if="attempt.executionId" :to="`/executions/${attempt.executionId}`" class="text-primary text-xs">View execution</RouterLink></div></li></ul>
            </ZhPanel>
        </template>
    </section>
</template>
